import { useCallback, useEffect, useRef, useState } from 'react';
import {
  newIdempotencyKey,
  ApiError,
  type ApiClient,
  type PoolOfferDetail,
  type VolatilityLevel,
} from '../../api';
import { gameEvents } from '../../postMessage';
import { instantPurchaseWithFairness } from '../../fairness/purchase';
import { useSession } from '../../session';
import { useAuth } from '../../auth/useAuth';
import { canAffordOffer, useWallet } from '../../wallet';
import { usePayments } from '../../payments/usePayments';
import { useIsSleek } from '../../theme';
import { holdPreOpenedCards } from '../../reveal';
import { trackBeginCheckout } from '../../tracking';
import { INSUFFICIENT_BALANCE_CODE, isLowBalanceLeftToHost } from '../../operatorLinks';
import { useGameFlow, type GameState } from './useGameFlow';
import {
  INSUFFICIENT_BALANCE_MESSAGE,
  purchaseErrorCode,
  purchaseErrorMessage,
} from './purchaseErrorCopy';

/**
 * `useGamePurchase` — single composed hook driving the buy-flow on a
 * Pack page. Classic and Modern both consume this so the orchestration
 * lives in exactly one place:
 *
 *   1. Owns `useGameFlow` (idle | purchasing | failed).
 *   2. Fires ONE `instant_purchase` (create + charge in a single
 *      round-trip), then hands the new `order_id` off to <RevealScreen>
 *      via `onRevealReady` (host parks the id in URL state and the reveal
 *      screen mounts and GETs the order itself, opening each card during
 *      the animation).
 *   3. Refreshes the wallet on completion.
 *   4. Implements the "quick-buy" queue: tapping "Rip Repackz" on a
 *      Just Pulled card calls `queueRip(offerId)`, which switches
 *      `quantity` to 1 and stashes the requested offer id; an internal
 *      effect then fires the purchase once the new offer has finished
 *      loading and is in an `available` state.
 *   5. Exposes `buy()` with the standard busy + status guards so
 *      consumers can wire it directly to a button's `onClick`.
 */

export interface UseGamePurchaseArgs {
  client: ApiClient;
  /** The offer id (URL game id or slider's active offer). Empty string
   *  is treated as "no offer selected yet" — purchase calls become no-ops. */
  offerId: string;
  /** The loaded offer detail used for status guards. `null` while loading. */
  offer: PoolOfferDetail | null;
  /** Current quantity from the BottomAction stepper. */
  quantity: number;
  /** Quantity setter — `queueRip` resets it to 1 because Just Pulled
   *  cards don't expose a stepper. */
  setQuantity: (next: number) => void;
  /** Called with the new `order_id` after a successful purchase. Hosts
   *  use this to park the id in URL state and trigger the reveal screen. */
  onRevealReady: (orderId: string) => void;
  /** Selected volatility level; drives the reveal distribution server-side.
   *  Optional so the classic template (no selector) can omit it — defaults to normal. */
  volatility?: VolatilityLevel;
}

export interface UseGamePurchaseApi {
  /** Wire to the main RIP! button. Includes busy + status guards. */
  buy: () => void;
  /** Wire to Just Pulled "Rip Repackz" taps. Caller is still
   *  responsible for switching the active offer + URL; this hook only
   *  queues the purchase. */
  queueRip: (offerId: number) => void;
  /** Wire to PurchaseOverlay's onRetry. Resets flow and re-fires. */
  retry: () => void;
  /** True while the flow is anything other than `idle`. */
  isBusy: boolean;
  /** Pass-through to PurchaseOverlay's `state` prop. */
  flow: GameState;
  /** Pass-through reset — consumers wire it to PurchaseOverlay's
   *  `onClose` (closes the failure screen). */
  resetFlow: () => void;
}

const AVAILABLE_STATUS = 'available';
function isOfferAvailable(status: string | null | undefined): boolean {
  return (status ?? AVAILABLE_STATUS) === AVAILABLE_STATUS;
}

export function useGamePurchase({
  client,
  offerId,
  offer,
  quantity,
  volatility = 'normal',
  setQuantity,
  onRevealReady,
}: UseGamePurchaseArgs): UseGamePurchaseApi {
  const wallet = useWallet();
  const { getToken } = useSession();
  const { requestAuth, registrationRequired, registered } = useAuth();
  const { requestTopUp, isStripeFunded } = usePayments();
  const flow = useGameFlow();
  // Sleek has the backend open the cards at purchase; the reveal still rips
  // them one by one off the held payload (reveal/sleek/preOpenedCards).
  const isSleek = useIsSleek();
  // True while `run()` has an outstanding fetch — independent of the
  // visible flow state so `retry()` (which calls `flow.reset()` first)
  // can't slip a second run in next to an in-flight first one.
  const inFlightRef = useRef(false);
  // Holds the current run's AbortController so an unmount cancels the
  // in-flight instant_purchase fetch instead of letting it land on a
  // stale closure.
  const abortRef = useRef<AbortController | null>(null);
  // Idempotency key for the CURRENT purchase attempt. Minted on a fresh
  // `run()`, REUSED by `retry()` (so a re-send collapses into the original
  // order server-side), cleared on success and on reset/unmount. Held in a
  // ref so it survives re-renders without re-triggering `run`.
  const idemKeyRef = useRef<string | null>(null);
  // True while `buy()` is confirming a low cached balance against a fresh
  // fetch. `isBusy`/`inFlightRef` don't cover that window, so without this a
  // double-tap could fire two overlapping checks (and two purchases).
  const checkingRef = useRef(false);

  // Cancel any in-flight purchase on unmount. Network responses that
  // arrive after we've left the page would otherwise set flow state on
  // a torn-down hook and (worse) emit purchase_completed for an order
  // the host can no longer route into reveal.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
      inFlightRef.current = false;
      idemKeyRef.current = null;
    };
  }, []);

  // Buy flow: ONE instant_purchase call (create + charge in a single
  // round-trip) → hand the new order off to reveal. UI shows the purchasing
  // spinner while the request runs; on success we close the overlay and the
  // consumer mounts <RevealScreen> via `onRevealReady` (reveal GETs the order
  // and opens each card during the animation). Emits postMessage so the parent
  // window can track progress. instant_purchase is strictly atomic — any
  // failure refunds — so every error here is safe to retry with the SAME
  // idempotency key.
  const run = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;

    // Auth gate — anonymous players (no token) and unregistered attach-mode
    // players must pass the auth modal before the purchase fires. A declined
    // modal is a quiet cancel: flow back to idle, no error screen. inFlightRef
    // stays set for the modal's duration so a second Buy tap can't re-enter.
    if (!getToken() || (registrationRequired && !registered)) {
      const ok = await requestAuth();
      if (!ok || controller.signal.aborted) {
        flow.reset();
        if (abortRef.current === controller) abortRef.current = null;
        inFlightRef.current = false;
        return;
      }
      // The gate may have adopted a DIFFERENT account (a mid-gameplay 401
      // dropped the token and the player re-authenticated). Drop any key
      // carried over from a prior attempt so a fresh one is minted below —
      // never replaying the previous account's idempotency key onto the new
      // user. Same-user live-token buys skip this block, keeping retry dedupe.
      idemKeyRef.current = null;
    }

    // Reuse the attempt's key if one is already set (a retry); otherwise
    // mint a fresh one for this new purchase — the moment a checkout starts
    // for the partner's pixels (a retry of the same attempt is not a new one).
    if (!idemKeyRef.current) {
      if (offer) trackBeginCheckout(offer, quantity);
      idemKeyRef.current = newIdempotencyKey();
    }
    const idemKey = idemKeyRef.current;

    // Move UI out of 'idle' immediately so the buy button disappears even
    // when the backend is slow (dev proxy can take seconds). The real
    // orderId replaces 'pending' once the order comes back.
    flow.startPurchase('pending');
    try {
      const order = await instantPurchaseWithFairness(client, offerId, quantity, volatility, idemKey, isSleek);
      if (controller.signal.aborted) return;
      if (isSleek) holdPreOpenedCards(order);
      flow.startPurchase(order.id);
      // Emit initiated + completed back-to-back with the real order id —
      // instant_purchase collapses the old create→purchase pair into one
      // call, so there's no intermediate id to report.
      gameEvents.purchaseInitiated(order.id);
      gameEvents.purchaseCompleted(
        order.id,
        order.poolItems.map((i) => ({
          id: i.id,
          name: i.title,
          rarity: i.badge?.name ?? 'common',
        })),
      );
      // Attempt done — drop the key so the next purchase mints a fresh one.
      idemKeyRef.current = null;
      flow.reset();
      void wallet.refresh();
      onRevealReady(order.id);
    } catch (err) {
      if (controller.signal.aborted) return;
      // On INSUFFICIENT_BALANCE, open the deposit screen; if the player funds
      // and the deposit is confirmed, retry the purchase ONCE with a fresh key.
      if (err instanceof ApiError && err.code === 'INSUFFICIENT_BALANCE') {
        const packPriceCents = Math.round(parseFloat(offer?.price ?? '0') * 100);
        const funded = await requestTopUp(packPriceCents);
        if (controller.signal.aborted) return;
        if (funded) {
          idemKeyRef.current = newIdempotencyKey();
          try {
            const retryOrder = await instantPurchaseWithFairness(
              client,
              offerId,
              quantity,
              volatility,
              idemKeyRef.current,
              isSleek,
            );
            if (controller.signal.aborted) return;
            if (isSleek) holdPreOpenedCards(retryOrder);
            flow.startPurchase(retryOrder.id);
            gameEvents.purchaseInitiated(retryOrder.id);
            gameEvents.purchaseCompleted(
              retryOrder.id,
              retryOrder.poolItems.map((i) => ({
                id: i.id,
                name: i.title,
                rarity: i.badge?.name ?? 'common',
              })),
            );
            idemKeyRef.current = null;
            flow.reset();
            void wallet.refresh();
            onRevealReady(retryOrder.id);
            return;
          } catch (retryErr) {
            if (controller.signal.aborted) return;
            const retryRaw = retryErr instanceof Error ? retryErr.message : 'unknown';
            gameEvents.purchaseFailed(null, retryRaw, purchaseErrorCode(retryErr));
            flow.fail(purchaseErrorMessage(retryErr), purchaseErrorCode(retryErr));
            return;
          }
        }
        if (isStripeFunded) {
          // The deposit screen already explained why the purchase can't go
          // through; the player closed it without paying, so don't repeat
          // the message — just drop back to idle.
          idemKeyRef.current = null;
          flow.reset();
          return;
        }
      }
      // Telemetry keeps the raw backend message; the UI shows reassuring,
      // retry-friendly copy (atomicity guarantees no lost charge).
      const rawMessage = err instanceof Error ? err.message : 'unknown';
      gameEvents.purchaseFailed(null, rawMessage, purchaseErrorCode(err));
      // The host tops up on its own and just got purchase_failed: back to
      // idle like a dismissed failure screen, no second popup from us.
      if (isLowBalanceLeftToHost(purchaseErrorCode(err))) {
        idemKeyRef.current = null;
        flow.reset();
        return;
      }
      // Keep idemKeyRef set: retry() re-sends with the SAME key so the
      // backend collapses a duplicate into the original (refunded) order.
      flow.fail(purchaseErrorMessage(err), purchaseErrorCode(err));
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      inFlightRef.current = false;
    }
  }, [
    client,
    offerId,
    quantity,
    volatility,
    offer,
    flow,
    wallet,
    getToken,
    requestAuth,
    registrationRequired,
    registered,
    requestTopUp,
    isStripeFunded,
    isSleek,
    onRevealReady,
  ]);

  const [pendingRipOfferId, setPendingRipOfferId] = useState<number | null>(null);
  const isBusy = flow.state.step !== 'idle';

  // Fire the queued purchase from a Just Pulled "Rip Repackz" tap once
  // the new offer has finished loading. Same gating as the on-page RIP
  // button: status check, busy guard, then `run()`.
  useEffect(() => {
    if (pendingRipOfferId === null) return;
    if (offerId !== String(pendingRipOfferId)) return;
    if (!offer) return;
    if (!isOfferAvailable(offer.status)) {
      setPendingRipOfferId(null);
      return;
    }
    if (flow.state.step !== 'idle') return;
    setPendingRipOfferId(null);
    void run();
  }, [pendingRipOfferId, offerId, offer, flow.state.step, run]);

  const buy = useCallback(() => {
    if (isBusy || inFlightRef.current || checkingRef.current) return;
    if (offer && !isOfferAvailable(offer.status)) return;
    // Stripe operators can top up in-iframe: never dead-end on a cached
    // "can't afford" — let run() hit the server, get INSUFFICIENT_BALANCE and
    // open the deposit screen. The client-side pre-check (fast-fail message)
    // is only for seamless operators, who have no in-iframe way to add funds.
    if (!offer || isStripeFunded || canAffordOffer(offer.price, quantity, wallet.balance_cents)) {
      void run();
      return;
    }
    // Seamless + cached balance says no — but it goes stale easily (we only
    // refresh on init / purchase / sell, and the parent isn't required to tell
    // us about a top-up), and refusing a player who just added funds is worse
    // than the error we're trying to avoid. Confirm against a fresh number
    // first; the extra round-trip only lands on the path that was going to
    // error anyway.
    checkingRef.current = true;
    flow.startPurchase('pending');
    void wallet
      .refresh()
      .then((fresh) => {
        if (canAffordOffer(offer.price, quantity, fresh)) {
          void run();
        } else {
          // The pre-check never reaches the server, so tell the parent here
          // too — otherwise a seamless casino never hears about the short
          // balance and can't open its own cashier.
          gameEvents.purchaseFailed(null, INSUFFICIENT_BALANCE_MESSAGE, INSUFFICIENT_BALANCE_CODE);
          if (isLowBalanceLeftToHost(INSUFFICIENT_BALANCE_CODE)) flow.reset();
          else flow.fail(INSUFFICIENT_BALANCE_MESSAGE, INSUFFICIENT_BALANCE_CODE);
        }
      })
      .finally(() => {
        checkingRef.current = false;
      });
  }, [isBusy, offer, quantity, wallet, flow, run, isStripeFunded]);

  const queueRip = useCallback(
    (id: number) => {
      setQuantity(1);
      setPendingRipOfferId(id);
    },
    [setQuantity],
  );

  // Only meaningful from the FailureScreen — guard so a rapid double-tap
  // can't slip a second `run()` in past the inFlightRef check. Leaves
  // `idemKeyRef` intact so `run()` re-sends with the SAME key (dedupe).
  const retry = useCallback(() => {
    if (inFlightRef.current) return;
    if (flow.state.step !== 'failed') return;
    flow.reset();
    void run();
  }, [flow, run]);

  // Dismissing the failure screen abandons the attempt — clear the key so
  // the NEXT buy starts a fresh, un-deduped purchase.
  const resetFlow = useCallback(() => {
    idemKeyRef.current = null;
    flow.reset();
  }, [flow]);

  return {
    buy,
    queueRip,
    retry,
    isBusy,
    flow: flow.state,
    resetFlow,
  };
}
